import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartTooltip } from './ChartTooltip';

/**
 * Cuts a label to fit the axis, keeping the start.
 *
 * Recharts does not wrap or ellipsize axis ticks: a name longer than the
 * reserved width is simply drawn past the edge and clipped, which is how
 * 'ciudad_de_mexico' lost its first letter. The tooltip still carries the whole
 * name.
 */
const MAX_LABEL = 15;

// One argument on purpose: recharts calls `tickFormatter(value, index)`, so a
// second parameter with a default silently receives the tick's position — which
// truncated the first label to 15 characters, the second to none, the third to
// one, and so on down the axis.
const truncate = (value) =>
  typeof value === 'string' && value.length > MAX_LABEL
    ? `${value.slice(0, MAX_LABEL - 1)}…`
    : value;

/**
 * A horizontal ranking. Top communes and shipments per company are the same
 * chart with a different label and a different fill.
 *
 * Horizontal because the labels are place and company names — vertical bars
 * would need them rotated, and rotated text is where dashboards go to die.
 *
 * The backend already cut this to its top entries: a `LIMIT` cannot live inside
 * a grouping set, so the cut happens after the fold, but before the wire.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.subtitle
 * @param {Array<{value: string, count: number}>} props.data
 * @param {(entry: {value: string}, index: number) => string} props.colorFor
 * @param {string} props.emptyMessage
 */
export const RankingCard = ({
  title,
  subtitle,
  data = [],
  colorFor,
  emptyMessage,
  isLoading,
}) => {
  const hasData = data.length > 0;
  // Recharts needs a name key; giving each row its colour here keeps the
  // tooltip and the bar reading from the same place.
  const rows = data.map((item, index) => ({
    name: item.value,
    count: item.count,
    color: colorFor(item, index),
  }));

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border">
      <div className="mb-5">
        <h2 className="text-base font-bold text-ninja-dark dark:text-white">{title}</h2>
        {subtitle && <p className="text-sm text-text-secondary mt-1">{subtitle}</p>}
      </div>

      <div style={{ height: Math.max(160, rows.length * 34) }}>
        {isLoading && !hasData && (
          <div className="h-full rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] animate-pulse" />
        )}

        {!isLoading && !hasData && (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-text-muted">{emptyMessage}</p>
          </div>
        )}

        {hasData && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <XAxis type="number" hide allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                width={104}
                tickFormatter={truncate}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12 }}
                stroke="currentColor"
                className="text-text-secondary"
              />
              <Tooltip
                cursor={{ fill: 'currentColor', className: 'text-ninja-gray dark:text-[#2d2d2d]', opacity: 0.4 }}
                content={(props) => <ChartTooltip {...props} title={props.label} />}
              />
              <Bar dataKey="count" name="Envíos" radius={[0, 6, 6, 0]} maxBarSize={22}>
                {rows.map((row) => (
                  <Cell key={row.name} fill={row.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
